import React from 'react'
import { AlertCircle, Loader2, Trash2, Calendar, ChevronDown, FileText, Download } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'
import ErrorBanner from '@/components/ErrorBanner'
import type { useTurbineDetailViewModel } from '../useTurbineDetailViewModel'

type VM = ReturnType<typeof useTurbineDetailViewModel>

interface ReportSectionProps {
  isCustomer: VM['isCustomer']
  selectedYear: VM['selectedYear']
  weekOptions: VM['weekOptions']
  yearOptions: VM['yearOptions']
  selectedReports: VM['selectedReports']
  uploadingReports: VM['uploadingReports']
  reportUploadError: VM['reportUploadError']
  existingReports: VM['existingReports']
  loadingReports: VM['loadingReports']
  reportYear: VM['reportYear']
  setReportYear: VM['setReportYear']
  reportWeek: VM['reportWeek']
  setReportWeek: VM['setReportWeek']
  isReportYearDropdownOpen: VM['isReportYearDropdownOpen']
  setIsReportYearDropdownOpen: VM['setIsReportYearDropdownOpen']
  isReportWeekDropdownOpen: VM['isReportWeekDropdownOpen']
  setIsReportWeekDropdownOpen: VM['setIsReportWeekDropdownOpen']
  reportWeekDropdownRef: VM['reportWeekDropdownRef']
  handleReportSelect: VM['handleReportSelect']
  removeSelectedReport: VM['removeSelectedReport']
  uploadReports: VM['uploadReports']
  deleteReport: VM['deleteReport']
  cleanFileName: VM['cleanFileName']
  /** Rapor listesi yuklenemediginde dolu gelir. "Rapor yok" ile KARISTIRILMAZ. */
  reportsLoadError: VM['reportsLoadError']
  retryLoadReports: VM['retryLoadReports']
}

const ReportSection: React.FC<ReportSectionProps> = ({
  isCustomer,
  selectedYear,
  weekOptions,
  yearOptions,
  selectedReports,
  uploadingReports,
  reportUploadError,
  existingReports,
  loadingReports,
  reportYear,
  setReportYear,
  reportWeek,
  setReportWeek,
  isReportYearDropdownOpen,
  setIsReportYearDropdownOpen,
  isReportWeekDropdownOpen,
  setIsReportWeekDropdownOpen,
  reportWeekDropdownRef,
  handleReportSelect,
  removeSelectedReport,
  uploadReports,
  deleteReport,
  cleanFileName,
  reportsLoadError,
  retryLoadReports,
}) => {
  const { t } = useLanguage()

  return (
    <>
          {/* Türbin Rapor Yükleme Alanı - Hidden for Customer */}
          {!isCustomer && (
          <div className="mt-8 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-6">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Türbin Raporu</h3>
                <p className="text-sm text-orange-600">PDF rapor dosyalarını yükleyin (Seçili yıl: {selectedYear})</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* PDF Upload Area */}
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-end gap-2 mb-4">
                  {/* Report Year Chip */}
                  <div className="relative">
                    <button
                      onClick={() => setIsReportYearDropdownOpen(!isReportYearDropdownOpen)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-orange-100 text-orange-700 hover:bg-orange-200 transition-all"
                    >
                      <Calendar className="w-3.5 h-3.5" />
                      <span>{reportYear}</span>
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isReportYearDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isReportYearDropdownOpen && (
                      <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] max-h-60 overflow-y-auto min-w-[120px]">
                        {yearOptions.map(year => (
                          <button
                            key={year}
                            onClick={() => {
                              setReportYear(year)
                              setIsReportYearDropdownOpen(false)
                            }}
                            className={`w-full px-4 py-2 text-left text-sm hover:bg-orange-50 first:rounded-t-lg last:rounded-b-lg transition-colors ${
                              reportYear === year ? 'bg-orange-100 text-orange-700 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {year}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Report Week Chip */}
                  <div className="relative" ref={reportWeekDropdownRef}>
                    <button
                      onClick={() => setIsReportWeekDropdownOpen(!isReportWeekDropdownOpen)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        reportWeek !== 0
                          ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <span>{reportWeek === 0 ? 'Tüm Haftalar' : `${reportWeek}. Hafta`}</span>
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isReportWeekDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isReportWeekDropdownOpen && (
                      <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] max-h-60 overflow-y-auto min-w-[140px]">
                        <button
                          onClick={() => {
                            setReportWeek(0)
                            setIsReportWeekDropdownOpen(false)
                          }}
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-orange-50 first:rounded-t-lg transition-colors ${
                            reportWeek === 0 ? 'bg-orange-100 text-orange-700 font-medium' : 'text-gray-700'
                          }`}
                        >
                          Tüm Haftalar
                        </button>
                        {weekOptions.map(week => (
                          <button
                            key={week}
                            onClick={() => {
                              setReportWeek(week)
                              setIsReportWeekDropdownOpen(false)
                            }}
                            className={`w-full px-4 py-2 text-left text-sm hover:bg-orange-50 last:rounded-b-lg transition-colors ${
                              reportWeek === week ? 'bg-orange-100 text-orange-700 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {week}. Hafta
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="border-2 border-dashed border-orange-300 bg-white/50 rounded-lg p-6 text-center">
                  <svg className="w-12 h-12 text-orange-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-orange-600 font-medium mb-2">PDF Rapor Yükle</p>
                  <p className="text-sm text-gray-500 mb-3">PDF formatında rapor dosyalarını sürükle & bırak (Seçili yıl: {reportYear})</p>
                  <input
                    type="file"
                    accept=".pdf"
                    multiple
                    className="hidden"
                    id="report-upload"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length > 0) {
                        handleReportSelect(files);
                      }
                    }}
                  />
                  <label
                    htmlFor="report-upload"
                    className="inline-block px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg cursor-pointer transition-colors"
                  >
                    Dosya Seç
                  </label>
                </div>

                {/* Error Message */}
                <ErrorBanner message={reportUploadError} />

                {/* Selected PDFs Preview */}
                {selectedReports.length > 0 && (
                  <div className="bg-white/50 rounded-lg border border-orange-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">Seçili Raporlar ({selectedReports.length})</h4>
                      <button
                        onClick={uploadReports}
                        disabled={uploadingReports}
                        className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white rounded-lg text-sm font-medium transition-colors flex items-center"
                      >
                        {uploadingReports ? (
                          <>
                            <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Yükleniyor...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            Raporları Yükle
                          </>
                        )}
                      </button>
                    </div>
                    
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {selectedReports.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-white/70 rounded-lg p-3 border">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{file.name}</p>
                              <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                            </div>
                          </div>
                          <button
                            onClick={() => removeSelectedReport(index)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Existing Reports */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Mevcut Raporlar ({reportYear}{reportWeek > 0 ? ` - ${reportWeek}. Hafta` : ''})</h4>
                {/* Elde onceki liste varsa veri KORUNUR, ustune uyari bandi cikar. */}
                {reportsLoadError && existingReports.length > 0 && (
                  <ErrorBanner message={reportsLoadError} tone="warning" onRetry={retryLoadReports} />
                )}
                <div className="border-2 border-dashed border-orange-300 bg-white/50 rounded-lg p-4 max-h-64 overflow-y-auto">
                  {loadingReports ? (
                    <div className="text-center py-8">
                      <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-orange-500" />
                      <p className="text-sm text-gray-500">{t.loading.reportsLoading}</p>
                    </div>
                  ) : reportsLoadError && existingReports.length === 0 ? (
                    /* Kural 4: "yuklenemedi" ile "henuz rapor yok" gorsel olarak AYRISIR. */
                    <div className="text-center py-8">
                      <AlertCircle className="w-8 h-8 mx-auto mb-2 text-red-400" />
                      <p className="text-sm text-gray-700 font-medium">{t.errors.loadFailed}</p>
                      <p className="text-xs text-gray-500 mt-1">{reportsLoadError}</p>
                      <button
                        type="button"
                        onClick={retryLoadReports}
                        className="mt-3 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-xs font-medium transition-colors"
                      >
                        {t.errors.retry}
                      </button>
                    </div>
                  ) : existingReports.length > 0 ? (
                    <div className="space-y-3">
                      {existingReports.map((report, index) => (
                        <div key={report.id} className="flex items-center justify-between bg-white/70 rounded-lg p-3 border border-orange-100 hover:border-orange-200 transition-colors">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                              <FileText className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{cleanFileName(report.original_filename)}</p>
                              <p className="text-xs text-gray-500">
                                {(report.file_size_bytes / 1024 / 1024).toFixed(2)} MB • 
                                {new Date(report.uploaded_at).toLocaleDateString('tr-TR')} • 
                                {report.first_name} {report.last_name}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <a
                              href={report.report_url}
                              download={cleanFileName(report.original_filename)}
                              className="text-green-600 hover:text-green-700 transition-colors"
                              title="Raporu indir"
                            >
                              <Download className="w-4 h-4" />
                            </a>
                            <button
                              onClick={() => deleteReport(report.id)}
                              className="text-red-600 hover:text-red-700 transition-colors"
                              title="Raporu sil"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-400">
                      <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-sm">Henüz rapor yüklenmemiş</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          )}
    </>
  )
}

export default ReportSection
